import React, { useState, useEffect } from 'react';
import { dashboardApi, trainingsApi, certificatesApi, usersApi, companiesApi } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import WorkerRutSearch from '../../components/WorkerRutSearch';
import {
  Users,
  Chalkboard,
  Certificate,
  TrendUp,
  Calendar,
  Clock,
  ArrowRight,
  Buildings,
  ChartDonut
} from '@phosphor-icons/react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';

const COLORS = ['#2BBAA4', '#2B4B8C', '#F59E0B', '#EF4444', '#8B5CF6'];

const StatCard = ({ icon: Icon, title, value, subtitle, trend, color = '#2BBAA4' }) => (
  <Card className="bg-white border-[#2B4B8C]/10 hover:border-[#2BBAA4]/50 transition-all duration-300 shadow-sm">
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-[#2B4B8C]/60 uppercase tracking-wider mb-1">{title}</p>
          <p className="text-3xl font-bold text-[#2B4B8C]">{value}</p>
          {subtitle && <p className="text-sm text-[#2B4B8C]/50 mt-1">{subtitle}</p>}
        </div>
        <div 
          className="w-12 h-12 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${color}15` }}
        >
          <Icon size={24} style={{ color }} weight="duotone" />
        </div>
      </div>
      {trend !== undefined && (
        <div className="mt-4 flex items-center gap-1 text-sm">
          <TrendUp className={trend >= 0 ? 'text-green-500' : 'text-red-500'} size={16} />
          <span className={trend >= 0 ? 'text-green-500' : 'text-red-500'}>
            {trend >= 0 ? '+' : ''}{trend}%
          </span>
          <span className="text-[#2B4B8C]/50">vs mes anterior</span>
        </div>
      )}
    </CardContent>
  </Card>
);

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [recentTrainings, setRecentTrainings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, trainingsRes] = await Promise.all([
        dashboardApi.admin(),
        trainingsApi.getAll({ page_size: 5 })
      ]);
      
      setStats(statsRes.data);
      const tData = trainingsRes.data.items || trainingsRes.data;
      setRecentTrainings(Array.isArray(tData) ? tData.slice(0, 5) : []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Error al cargar datos del dashboard');
    } finally {
      setLoading(false);
    }
  };

  const trainingsByTypeData = stats?.trainings_by_type 
    ? Object.entries(stats.trainings_by_type).map(([name, value]) => ({ name: name.slice(0, 15), value }))
    : [];

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { label: 'Borrador', className: 'bg-gray-100 text-gray-600 border-gray-200' },
      published: { label: 'Publicada', className: 'bg-blue-50 text-blue-600 border-blue-200' },
      in_progress: { label: 'En Curso', className: 'bg-yellow-50 text-yellow-600 border-yellow-200' },
      closed: { label: 'Cerrada', className: 'bg-orange-50 text-orange-600 border-orange-200' },
      completed: { label: 'Completada', className: 'bg-[#2BBAA4]/10 text-[#2BBAA4] border-[#2BBAA4]/30' },
    };
    const config = statusConfig[status] || statusConfig.draft;
    return <Badge className={`${config.className} border`}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2BBAA4]" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="admin-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-[#2B4B8C]">Dashboard</h1>
        <p className="text-[#2B4B8C]/60 mt-1">Vista general de la plataforma de capacitaciones</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          icon={Users}
          title="Trabajadores"
          value={stats?.total_workers || 0}
          subtitle="Usuarios registrados"
          color="#2BBAA4"
        />
        <StatCard
          icon={Chalkboard}
          title="Capacitaciones"
          value={stats?.total_trainings || 0}
          subtitle={`${stats?.pending_trainings || 0} pendientes`}
          color="#2B4B8C"
        />
        <StatCard
          icon={Certificate}
          title="Certificados"
          value={stats?.total_certificates || 0}
          subtitle="Emitidos"
          color="#F59E0B"
        />
        <StatCard
          icon={TrendUp}
          title="Tasa de Aprobación"
          value={`${stats?.approval_rate || 0}%`}
          subtitle="Promedio general"
          color="#8B5CF6"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Trainings by Type */}
        <Card className="bg-white border-[#2B4B8C]/10 shadow-sm">
          <CardHeader>
            <CardTitle className="text-[#2B4B8C] flex items-center gap-2">
              <ChartDonut size={20} className="text-[#2BBAA4]" />
              Capacitaciones por Tipo
            </CardTitle>
          </CardHeader>
          <CardContent>
            {trainingsByTypeData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={trainingsByTypeData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {trainingsByTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid rgba(43,75,140,0.1)',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                    labelStyle={{ color: '#2B4B8C' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-[#2B4B8C]/50">
                Sin datos disponibles
              </div>
            )}
            {/* Legend */}
            <div className="flex flex-wrap gap-4 justify-center mt-4">
              {trainingsByTypeData.map((entry, index) => (
                <div key={entry.name} className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  />
                  <span className="text-sm text-[#2B4B8C]/70">{entry.name}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recent Trainings */}
        <Card className="bg-white border-[#2B4B8C]/10 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-[#2B4B8C] flex items-center gap-2">
              <Calendar size={20} className="text-[#2BBAA4]" />
              Capacitaciones Recientes
            </CardTitle>
            <Button variant="ghost" size="sm" className="text-[#2BBAA4] hover:text-[#2BBAA4]/80">
              Ver todas <ArrowRight className="ml-1" size={16} />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentTrainings.length > 0 ? recentTrainings.map((training) => (
                <div 
                  key={training.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-[#f8fafc] hover:bg-[#f1f5f9] transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-[#2B4B8C] font-medium truncate">{training.name}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-xs text-[#2B4B8C]/50 flex items-center gap-1">
                        <Clock size={12} />
                        {format(new Date(training.scheduled_date), "dd MMM yyyy", { locale: es })}
                      </span>
                      <span className="text-xs text-[#2B4B8C]/50">
                        {training.enrolled_count}/{training.max_participants} inscritos
                      </span>
                    </div>
                  </div>
                  {getStatusBadge(training.status)}
                </div>
              )) : (
                <p className="text-[#2B4B8C]/50 text-center py-8">No hay capacitaciones recientes</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="bg-white border-[#2B4B8C]/10 shadow-sm">
        <CardHeader>
          <CardTitle className="text-[#2B4B8C]">Acciones Rápidas</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Button 
              variant="outline" 
              className="h-auto py-6 flex flex-col gap-2 border-[#2B4B8C]/10 hover:border-[#2BBAA4]/50 hover:bg-[#2BBAA4]/5 text-[#2B4B8C]"
              onClick={() => window.location.href = '/admin/trainings'}
            >
              <Chalkboard size={24} className="text-[#2BBAA4]" />
              <span>Nueva Capacitación</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-6 flex flex-col gap-2 border-[#2B4B8C]/10 hover:border-[#2B4B8C]/50 hover:bg-[#2B4B8C]/5 text-[#2B4B8C]"
              onClick={() => window.location.href = '/admin/users'}
            >
              <Users size={24} className="text-[#2B4B8C]" />
              <span>Gestionar Usuarios</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-6 flex flex-col gap-2 border-[#2B4B8C]/10 hover:border-[#F59E0B]/50 hover:bg-[#F59E0B]/5 text-[#2B4B8C]"
              onClick={() => window.location.href = '/admin/companies'}
            >
              <Buildings size={24} className="text-[#F59E0B]" />
              <span>Gestionar Empresas</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-6 flex flex-col gap-2 border-[#2B4B8C]/10 hover:border-[#8B5CF6]/50 hover:bg-[#8B5CF6]/5 text-[#2B4B8C]"
              onClick={() => window.location.href = '/admin/certificates'}
            >
              <Certificate size={24} className="text-[#8B5CF6]" />
              <span>Ver Certificados</span>
            </Button>
          </div>
        </CardContent>
      </Card>
      {/* RUT Search */}
      <WorkerRutSearch />
    </div>
  );
}